import { Injectable } from '@angular/core';
import '@tensorflow/tfjs-backend-webgl';
import { Human, type BackendEnum, type Config } from '@vladmandic/human';
import { Store } from '@ngrx/store';
import {
  selectPhotoById,
  selectShoppingCartContext,
} from '@ui/products/shopping-cart-back/actions/selector';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import Dexie from 'dexie';
import { maxSquareCalculator } from 'ZalamoLibrary/src/lib/zalamo/face-detection/calculateMaxSquare';

const database = new Dexie('zalamo-faces');
database.version(1).stores({
  faces: `&photoId`,
});
const cache = new Map<string, Observable<any>>();
const humanConfig: Partial<Config> = {
  // user configuration for human, used to fine-tune behavior
  debug: true,
  backend: 'humangl' as BackendEnum,
  modelBasePath: 'https://vladmandic.github.io/human-models/models/',
  filter: { enabled: false, equalization: false, flip: false },

  face: {
    enabled: true,
    detector: {
      rotation: true,
      maxDetected: 100,
      minConfidence: 0.58,
      return: false,
      enabled: false,
    },
    iris: { enabled: false },

    description: { enabled: false },
    emotion: { enabled: false },
    antispoof: { enabled: false },
    liveness: { enabled: false },
    mesh: { enabled: true },
    gear: { enabled: false },
    attention: { enabled: false },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: false },
  segmentation: { enabled: false },
};

@Injectable({
  providedIn: 'root',
})
export class FaceDetectorService {
  human: Human;

  constructor(private store: Store, private downloader: GetImageUrlService) {}

  async init() {
    this.human = new Human(humanConfig);
    await this.human.load();
    await this.human.warmup();
  }

  async detectFaces(img: string) {
    console.log(img);

    return await this.human
      .detect(
        await createImageBitmap(await fetch(img).then((res) => res.blob())),
        humanConfig
      )
      .then((r) => ({
        faces: r.face.map((f) => f.boxRaw),
        gravityCenter: r.face
          .map((f) => f.boxRaw)
          .reduce(
            (a, b) => [
              a[0] === null ? b[0] : Math.min(a[0], b[0]),
              a[1] === null ? b[1] : Math.min(a[1], b[1]),
              a[2] === null ? b[0] + b[2] : Math.max(a[2], b[0] + b[2]),
              a[3] === null ? b[1] + b[3] : Math.max(a[3], b[1] + b[3]),
            ],
            [null, null, null, null]
          ),
      }));
  }

  detectFacesByPhotoId(
    photoId: string,
    containerRatio = 1
  ): Observable<{
    faces: any[];
    gravityCenter: [number, number, number, number];
    maxSquare: { x: number; y: number; w: number; h: number };
  }> {
    if (!photoId) {
      return of({
        faces: [],
        gravityCenter: null,
        maxSquare: null,
      });
    }
    return from(database.table('faces').get(photoId))
      .pipe(
        switchMap((r) => {
          if (r) {
            console.log('USE DB VERSION');
            return of(r);
          }
          if (cache.has(photoId)) {
            console.log('USE CACHE');
            return cache.get(photoId);
          }
          const result = combineLatest([
            this.store.select(selectPhotoById(photoId)),
            this.store.select(selectShoppingCartContext),
          ]).pipe(
            switchMap(([photo, { context, contextId }]) =>
              this.downloader
                .transform(
                  { photoId },
                  photo.photoGroupId,
                  'anyone',
                  context,
                  contextId,
                  200
                )
                .pipe(
                  map((img) => ({ img, ratio: photo.width / photo.height }))
                )
            ),
            switchMap((img) =>
              this.detectFaces(String(img.img).match(/blob:(.*?) /)[0]).then(
                (r) => ({
                  ...r,
                  ratio: img.ratio,
                })
              )
            ),
            tap((x) => console.log(x)),
            tap((x) => database.table('faces').add({ ...x, photoId }))
          );

          cache.set(photoId, result);

          return result;
        })
      )
      .pipe(
        switchMap((res) =>
          this.store
            .select(selectPhotoById(photoId))
            .pipe(map((r) => ({ ...res, ratio: r.width / r.height })))
        ),
        map(({ faces, gravityCenter, ratio }) => ({
          faces,
          gravityCenter,
          maxSquare: maxSquareCalculator(
            faces,
            gravityCenter,
            ratio,
            containerRatio
          ),
        }))
      );
  }
}
